import React, { useEffect, useState } from "react";
import { popsApi, fmtUSD } from "../../lib/api";
import { Button } from "../ui/button";
import { RefreshCcw, Zap, CheckCircle2, FileWarning } from "lucide-react";
import { toast } from "sonner";

const ACTION_LABELS = {
  credit_note: { label: "Credit note", cls: "bg-[#fee2e2] text-[#b91c1c]" },
  true_up_debit: { label: "True-up debit (next invoice)", cls: "bg-[#fef3c7] text-[#92400e]" },
  adjustment_next_invoice: { label: "Credit on next invoice", cls: "bg-[#e0f2fe] text-[#0369a1]" },
};

const STATUS_CLS = {
  proposed: "bg-[#e0f2fe] text-[#0369a1]",
  applied: "bg-[#dcfce7] text-[#15803d]",
  dismissed: "bg-[#e4e4e7] text-[#52525b]",
};

export default function RestatementsTab() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => popsApi.restatements().then(setItems).catch(console.error);
  useEffect(() => { load(); }, []);

  const simulate = async () => {
    setBusy(true);
    try {
      const r = await popsApi.simulateRestatement();
      toast.success(`Restatement detected — ${r.type_label} on ${r.services.join(", ")} (${r.pct > 0 ? "+" : ""}${r.pct}%)`, {
        description: `${r.proposals.length} customer proposal(s) generated for ${r.period}.`,
      });
      load();
    } catch (e) {
      toast.error("Simulation failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const apply = async (rid, p) => {
    try {
      const out = await popsApi.applyRestatement(rid, p.customer_id);
      const m = out.proposal?.applied;
      toast.success(
        m?.method === "credit_note"
          ? `Credit note ${m.ref} issued for ${p.customer_name}`
          : `${p.delta_customer_usd > 0 ? "True-up debit" : "Restatement credit"} added to ${p.customer_name}'s next invoice`,
      );
      load();
    } catch (e) {
      toast.error("Apply failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const dismiss = async (rid, p) => {
    try {
      await popsApi.dismissRestatement(rid, p.customer_id, "dismissed from UI");
      toast.success(`Proposal for ${p.customer_name} dismissed`);
      load();
    } catch (e) {
      toast.error("Dismiss failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  if (!items) return <div className="text-muted-foreground text-sm py-8">Loading restatements…</div>;

  return (
    <div className="space-y-4" data-testid="pops-restatements-tab">
      <div className="cs-card p-4 flex flex-wrap items-center gap-3">
        <FileWarning className="w-4 h-4 text-[#00579e]" />
        <div>
          <div className="cs-heading text-sm font-semibold">Provider restatement true-ups</div>
          <div className="text-[11px] text-muted-foreground">
            When a closed month's provider bill changes (CUD retro-credits, SUD recalcs), the rated per-customer delta is
            computed and a credit note or true-up line is proposed automatically.
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={load} className="rounded-full h-9 text-xs px-4" data-testid="refresh-restatements">
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Re-detect
          </Button>
          <Button onClick={simulate} disabled={busy} data-testid="simulate-restatement-button"
            className="rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-5">
            <Zap className="w-4 h-4 mr-1.5" /> {busy ? "Simulating…" : "Simulate restatement"}
          </Button>
        </div>
      </div>

      {items.length === 0 && (
        <div className="cs-card p-10 text-center text-muted-foreground text-sm" data-testid="no-restatements">
          No provider restatements detected.
        </div>
      )}

      {items.map((r) => (
        <div key={r.id} className="cs-card p-5" data-testid={`restatement-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-[0.1em] ${r.pct < 0 ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#fef3c7] text-[#92400e]"}`}>
              {r.type_label}
            </span>
            <span className="cs-mono text-xs font-semibold">{r.period}</span>
            <span className="text-xs text-muted-foreground">· {r.provider} · {r.services.join(", ")} · </span>
            <span className={`cs-mono text-xs font-semibold ${r.pct < 0 ? "text-[#15803d]" : "text-[#b45309]"}`}>
              {r.pct > 0 ? "+" : ""}{r.pct}%
            </span>
            <span className={`ml-auto px-2 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${r.status === "resolved" ? "bg-[#dcfce7] text-[#15803d]" : "bg-[#e0f2fe] text-[#0369a1]"}`}>
              {r.status}
            </span>
          </div>
          <div className="text-[11px] text-muted-foreground mb-3">
            {r.description} <span className="cs-mono">({r.source} · detected {(r.detected_at || "").slice(0, 10)})</span>
          </div>

          <table className="w-full text-xs">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                {["Customer", "Provider Δ", "Customer Δ (rated)", "Closed invoice", "Proposed action", "Status", ""].map((h) => (
                  <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.proposals.map((p) => {
                const act = ACTION_LABELS[p.proposed_action] || {};
                return (
                  <tr key={p.customer_id} className="border-b border-border last:border-0" data-testid={`proposal-${r.id}-${p.customer_id}`}>
                    <td className="px-3 py-2 font-medium">{p.customer_name}</td>
                    <td className={`px-3 py-2 cs-mono ${p.delta_effective_usd < 0 ? "text-[#15803d]" : "text-[#b45309]"}`}>
                      {p.delta_effective_usd > 0 ? "+" : ""}{fmtUSD(p.delta_effective_usd)}
                    </td>
                    <td className={`px-3 py-2 cs-mono font-semibold ${p.delta_customer_usd < 0 ? "text-[#15803d]" : "text-[#b45309]"}`}>
                      {p.delta_customer_usd > 0 ? "+" : ""}{fmtUSD(p.delta_customer_usd)}
                    </td>
                    <td className="px-3 py-2 cs-mono text-[11px]">
                      {p.invoice_number ? `${p.invoice_number} (${p.invoice_state})` : <span className="text-muted-foreground">none issued</span>}
                    </td>
                    <td className="px-3 py-2">
                      {p.status === "proposed" && act.label && (
                        <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${act.cls}`}>{act.label}</span>
                      )}
                      {p.status === "applied" && p.applied && (
                        <span className="text-[11px] text-[#15803d] flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {p.applied.method === "credit_note" ? `credit note ${p.applied.ref}` : "adjustment queued for next invoice"}
                        </span>
                      )}
                      {p.status === "dismissed" && <span className="text-[11px] text-muted-foreground">dismissed</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${STATUS_CLS[p.status]}`}>{p.status}</span>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {p.status === "proposed" && (
                        <>
                          <button onClick={() => apply(r.id, p)} data-testid={`apply-${r.id}-${p.customer_id}`}
                            className="text-[11px] text-[#00579e] font-semibold hover:underline">
                            {p.proposed_action === "credit_note" ? "Create credit note" : p.proposed_action === "true_up_debit" ? "Add true-up" : "Add credit line"}
                          </button>
                          <button onClick={() => dismiss(r.id, p)} data-testid={`dismiss-${r.id}-${p.customer_id}`}
                            className="text-[11px] text-muted-foreground hover:underline ml-3">
                            dismiss
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
